import { useEffect, useState } from "react";
import { Building2, Link2, Plus } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const EMPTY = { name: "", slug: "", contact_email: "", phone: "", admin_name: "", admin_email: "", admin_password: "" };

export default function Companies() {
  const [items, setItems] = useState([]);
  const [plans, setPlans] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ id: "", name: "", slug: "", contact_email: "" });
  const [denied, setDenied] = useState(false);

  const load = () => api.get("/companies").then((r) => setItems(r.data)).catch((e) => {
    if (e.response?.status === 403) setDenied(true);
    else toast.error(formatApiError(e));
  });
  useEffect(() => { load(); api.get("/platform/plans").then((r) => setPlans(r.data)).catch(() => {}); }, []);

  const onboard = async (e) => {
    e.preventDefault();
    try {
      await api.post("/companies", form);
      toast.success(`${form.name} onboarded — admin login created`);
      setOpen(false);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const openEdit = (c) => { setEditForm({ id: c.id, name: c.name, slug: c.slug, contact_email: c.contact_email || "" }); setEditOpen(true); };
  const saveEdit = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/companies/${editForm.id}`, { name: editForm.name, slug: editForm.slug, contact_email: editForm.contact_email });
      toast.success("Business details updated");
      setEditOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggleActive = async (c) => {
    try {
      await api.patch(`/companies/${c.id}`, { active: !c.active });
      toast.success(c.active ? "Hub taken offline" : "Hub reactivated");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const copyHub = (c) => {
    navigator.clipboard.writeText(`${window.location.origin}/hub/${c.slug}`);
    toast.success("Hub link copied");
  };

  const setPlan = async (c, planId) => {
    try {
      await api.patch(`/companies/${c.id}`, { plan_id: planId === "none" ? null : planId, plan_status: planId === "none" ? "none" : "active" });
      toast.success("Plan updated");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggleWaived = async (c) => {
    try {
      await api.patch(`/companies/${c.id}`, { fees_waived: !c.fees_waived });
      toast.success(c.fees_waived ? "Fees reinstated" : "Fees waived — full access granted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const impersonate = async (c) => {
    try {
      const { data } = await api.post(`/platform/impersonate/${c.id}`);
      localStorage.setItem("super_token_backup", localStorage.getItem("token"));
      localStorage.setItem("token", data.access_token);
      toast.success(`Logged in as ${c.name}`);
      window.location.href = "/dashboard";
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (denied) return <div data-testid="companies-denied" className="border border-red-300 bg-red-50 px-6 py-4 text-sm text-red-700">Super-admin access required.</div>;

  return (
    <div data-testid="companies-page" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Vendor Companies</h1>
          <p className="mt-1 text-sm text-muted-foreground">Platform administration — onboard catering businesses onto CateringDiary.</p>
        </div>
        <Button data-testid="onboard-company-button" onClick={() => setOpen(true)} className="rounded-md bg-ink hover:bg-ink-light">
          <Plus className="mr-2 h-4 w-4" /> Onboard Vendor
        </Button>
      </div>

      <div className="border border-border bg-white">
        <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left">
              {["Business", "Slug", "Users", "Bookings", "Plan", "Waived", "Status", "Actions"].map((h) => (
                <th key={h} className="px-5 py-3 text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.id} data-testid={`company-row-${c.id}`} className="border-b border-border last:border-0 hover:bg-slate-50">
                <td className="px-5 py-3">
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 items-center justify-center bg-muted"><Building2 className="h-4 w-4 text-ink" /></div>
                    <span className="font-medium">{c.name}</span>
                  </div>
                </td>
                <td className="px-5 py-3 font-mono text-xs">/hub/{c.slug}</td>
                <td className="px-5 py-3 tabular-nums">{c.user_count}</td>
                <td className="px-5 py-3 tabular-nums">{c.booking_count}</td>
                <td className="px-5 py-3">
                  <select data-testid={`plan-select-${c.id}`} value={c.plan_id || "none"} onChange={(e) => setPlan(c, e.target.value)}
                    className="border border-border bg-white px-2 py-1 text-xs">
                    <option value="none">Free tier</option>
                    {plans.map((p) => <option key={p.id} value={p.id}>{`${p.name} (£${p.price_monthly}/mo)`}</option>)}
                  </select>
                </td>
                <td className="px-5 py-3">
                  <button data-testid={`waive-fees-${c.id}`} onClick={() => toggleWaived(c)}
                    className={`rounded-full border px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-[0.15em] ${c.fees_waived ? "border-emerald-600 bg-emerald-50 text-emerald-700" : "border-gray-300 text-gray-500"}`}>
                    {c.fees_waived ? "Waived" : "Off"}
                  </button>
                </td>
                <td className="px-5 py-3">
                  <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-[0.15em] ${c.active ? "border-emerald-600 bg-emerald-50 text-emerald-700" : "border-gray-400 bg-gray-50 text-gray-600"}`} data-testid={`company-status-${c.id}`}>
                    {c.active ? "Live" : "Offline"}
                  </span>
                </td>
                <td className="px-5 py-3">
                  <div className="flex gap-2">
                    <Button data-testid={`edit-company-${c.id}`} size="sm" variant="outline" className="rounded-md" onClick={() => openEdit(c)}>Edit</Button>
                    <Button data-testid={`login-as-${c.id}`} size="sm" variant="outline" className="rounded-md border-brand text-brand hover:bg-emerald-50" onClick={() => impersonate(c)}>Login as</Button>
                    <Button data-testid={`copy-hub-${c.id}`} size="sm" variant="outline" className="rounded-md" onClick={() => copyHub(c)}><Link2 className="h-3.5 w-3.5" /></Button>
                    <Button data-testid={`toggle-company-${c.id}`} size="sm" variant="outline" className="rounded-md" onClick={() => toggleActive(c)}>{c.active ? "Offline" : "Activate"}</Button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={8} className="px-5 py-10 text-center text-muted-foreground">No vendors yet.</td></tr>}
          </tbody>
        </table>
        </div>
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-md bg-white" data-testid="edit-company-dialog">
          <DialogHeader><DialogTitle className="font-heading">Edit business details</DialogTitle></DialogHeader>
          <form onSubmit={saveEdit} className="mt-2 space-y-4">
            <div className="space-y-1.5"><Label>Business name</Label><Input data-testid="edit-name-input" required value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} /></div>
            <div className="space-y-1.5">
              <Label>Hub slug</Label>
              <Input data-testid="edit-slug-input" required value={editForm.slug} onChange={(e) => setEditForm({ ...editForm, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") })} />
              <p className="text-xs text-muted-foreground">Changes the public hub URL: /hub/{editForm.slug}</p>
            </div>
            <div className="space-y-1.5"><Label>Public contact email</Label><Input data-testid="edit-email-input" type="email" required value={editForm.contact_email} onChange={(e) => setEditForm({ ...editForm, contact_email: e.target.value })} /></div>
            <Button data-testid="edit-save-button" type="submit" className="w-full rounded-md bg-ink hover:bg-ink-light">Save changes</Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg bg-white" data-testid="onboard-dialog">
          <DialogHeader><DialogTitle className="font-heading">Onboard Vendor</DialogTitle></DialogHeader>
          <form onSubmit={onboard} className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Business name</Label><Input data-testid="company-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value, slug: form.slug || e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "") })} /></div>
            <div className="space-y-1.5"><Label>Hub slug</Label><Input data-testid="company-slug-input" required value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Public contact email</Label><Input data-testid="company-email-input" type="email" required value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Phone</Label><Input data-testid="company-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <div className="col-span-2 border-t border-border pt-3"><p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Vendor admin login</p></div>
            <div className="space-y-1.5"><Label>Admin name</Label><Input data-testid="admin-name-input" required value={form.admin_name} onChange={(e) => setForm({ ...form, admin_name: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Admin email</Label><Input data-testid="admin-email-input" type="email" required value={form.admin_email} onChange={(e) => setForm({ ...form, admin_email: e.target.value })} /></div>
            <div className="col-span-2 space-y-1.5"><Label>Temporary password</Label><Input data-testid="admin-password-input" required minLength={8} value={form.admin_password} onChange={(e) => setForm({ ...form, admin_password: e.target.value })} /></div>
            <div className="col-span-2"><Button data-testid="onboard-submit-button" type="submit" className="w-full rounded-md bg-ink hover:bg-ink-light">Create Vendor & Admin</Button></div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
